import { useReducer, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

type AuthState = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error' | 'retry';

interface AuthStateData {
  state: AuthState;
  retryCount: number;
  error: string | null;
  lastAttempt: number;
}

type AuthAction = 
  | { type: 'START_LOADING' }
  | { type: 'SET_AUTHENTICATED' }
  | { type: 'SET_UNAUTHENTICATED' }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'RETRY' }
  | { type: 'FORCE_COMPLETE' };

const authReducer = (state: AuthStateData, action: AuthAction): AuthStateData => {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, state: 'loading', error: null };
    case 'SET_AUTHENTICATED':
      return { ...state, state: 'authenticated', error: null, retryCount: 0 };
    case 'SET_UNAUTHENTICATED':
      return { ...state, state: 'unauthenticated', error: null, retryCount: 0 };
    case 'SET_ERROR':
      return { ...state, state: 'error', error: action.error };
    case 'RETRY':
      return { 
        ...state, 
        state: 'retry', 
        retryCount: state.retryCount + 1,
        lastAttempt: Date.now(),
        error: null 
      };
    case 'FORCE_COMPLETE':
      return { ...state, state: 'authenticated', error: null };
    default:
      return state;
  }
};

export const useAuthStateManager = () => {
  const { user, profile, loading } = useAuth();
  const [authData, dispatch] = useReducer(authReducer, {
    state: 'idle',
    retryCount: 0,
    error: null,
    lastAttempt: 0
  });

  const checkAuthState = useCallback((): void => {
    if (loading) {
      dispatch({ type: 'START_LOADING' });
      return;
    }

    if (user && profile) {
      dispatch({ type: 'SET_AUTHENTICATED' });
      return;
    }

    if (!user) {
      dispatch({ type: 'SET_UNAUTHENTICATED' });
      return;
    }

    dispatch({ type: 'SET_ERROR', error: 'Profil bilgileri yüklenemedi' });
  }, [user, profile, loading]);

  const retry = useCallback((): void => {
    if (authData.retryCount < 3) {
      dispatch({ type: 'RETRY' });
    } else {
      dispatch({ type: 'FORCE_COMPLETE' });
    }
  }, [authData.retryCount]);

  // Ana state machine logic
  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Retry state'den loading'e geçiş
  useEffect(() => {
    if (authData.state === 'retry') {
      const timer = setTimeout(() => {
        dispatch({ type: 'START_LOADING' });
      }, 1000 * authData.retryCount); // Progressive delay
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [authData.state, authData.retryCount]);

  // Timeout mekanizması
  useEffect(() => {
    if (authData.state === 'loading') {
      const timeout = setTimeout(() => {
        console.log('⚠️ Auth timeout, attempting retry');
        retry();
      }, 6000); // 6 saniye timeout
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [authData.state, retry]);

  return {
    state: authData.state,
    isReady: authData.state !== 'idle' && authData.state !== 'loading' && authData.state !== 'retry',
    error: authData.error,
    retryCount: authData.retryCount,
    retry,
    canProceed: authData.state === 'authenticated'
  };
};
